import { useEffect, useRef, useState, type ReactNode } from 'react'
import { COMPONENTS_SEARCH_SELECT_TEXTS as TX } from '../../shared/texts'

// A select you can type into: the list shows what `search` returns for the typed text, one
// entry is picked. Where the options come from is the caller's business — CustomerPicker asks
// the server (every customer reachable, not a first page), CountryPicker filters a local list.
// Keyboard: ↑/↓ move, Enter picks (and never submits the form), Esc closes.
//
// The current value is shown through `resolve`, never searched for: a stored value must
// appear even when it is nowhere among the first hits, or when no option knows it at all.
// Disabling works through the surrounding <fieldset disabled> — the input inherits it.

export interface SearchSelectProps<T> {
    value: string
    onChange: (value: string) => void
    search: (q: string) => Promise<T[]> | T[]
    resolve: (value: string) => Promise<T | null> | T | null
    keyOf: (item: T) => string
    labelOf: (item: T) => string
    renderOption?: (item: T) => ReactNode
    placeholder?: string
    debounceMs?: number
    // Offers an entry that sets the value to '' — for optional fields.
    clearLabel?: string
    name?: string
    // Changes when the underlying data changed elsewhere (e.g. useDataVersion('customers')):
    // the shown value is resolved again and an open list searches again.
    refreshKey?: unknown
    // A value must be picked — checked by forms/checkForm.ts (the input holds a label, not the
    // value, so the native `required` cannot judge it).
    required?: boolean
}

export default function SearchSelect<T>(props: SearchSelectProps<T>) {
    const { value, onChange, search, resolve, keyOf, labelOf, renderOption, placeholder, debounceMs = 0, clearLabel, name, refreshKey, required } = props
    const [selected, setSelected] = useState<T | null>(null)
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState<T[]>([])
    const [active, setActive] = useState(0)
    const [loading, setLoading] = useState(false)
    const seq = useRef(0)
    const listRef = useRef<HTMLUListElement>(null)

    // The clear entry (index 0) only while nothing is typed: with a search text the first HIT
    // must be the one Enter takes — otherwise Enter would wipe the value instead of picking.
    const showClear = !!clearLabel && !query.trim()
    const offset = showClear ? 1 : 0
    const count = options.length + offset

    const resolvedFor = useRef<unknown>(refreshKey)
    useEffect(() => {
        if (!value) { setSelected(null); return }
        if (selected && keyOf(selected) === value && resolvedFor.current === refreshKey) return
        resolvedFor.current = refreshKey
        let alive = true
        Promise.resolve(resolve(value))
            .then((item) => { if (alive) setSelected(item) })
            .catch(() => { if (alive) setSelected(null) })
        return () => { alive = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, refreshKey])

    // `seq` drops answers that arrive after a newer request went out, so a slow early reply
    // cannot overwrite a later one.
    useEffect(() => {
        if (!open) return
        const my = ++seq.current
        setLoading(true)
        const t = setTimeout(() => {
            // With a search text the first hit is marked. Without one the CURRENT value is marked
            // (and put in front when the first hits don't contain it), so Enter keeps it; with no
            // value nothing is marked and Enter on a merely focused field changes nothing.
            const typed = !!query.trim()
            Promise.resolve(search(query.trim()))
                .then((found) => {
                    if (my !== seq.current) return
                    let rows = found
                    let mark = typed ? 0 : -1
                    if (!typed && value && selected && keyOf(selected) === value) {
                        if (!rows.some((r) => keyOf(r) === value)) rows = [selected, ...rows]
                        mark = rows.findIndex((r) => keyOf(r) === value) + (clearLabel ? 1 : 0)
                    }
                    setOptions(rows)
                    setActive(mark)
                })
                .catch(() => { if (my === seq.current) setOptions([]) })
                .finally(() => { if (my === seq.current) setLoading(false) })
        }, debounceMs)
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, open, refreshKey])

    useEffect(() => {
        listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' })
    }, [active, options])

    function close() {
        setOpen(false)
        setQuery('')
    }

    function pickIndex(i: number) {
        if (showClear && i === 0) {
            setSelected(null)
            onChange('')
        } else {
            const item = options[i - offset]
            if (!item) return
            setSelected(item)
            onChange(keyOf(item))
        }
        close()
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!open) { setOpen(true); return }
            setActive((i) => Math.min(i + 1, count - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (open && active >= 0 && active < count) pickIndex(active)
        } else if (e.key === 'Escape') {
            if (open) { e.preventDefault(); e.stopPropagation(); close() }
        }
    }

    const shown = selected ? labelOf(selected) : ''
    const itemProps = (i: number, current: boolean) => ({
        role: 'option',
        'aria-selected': i === active,
        className: `picker-item${i === active ? ' active' : ''}${current ? ' current' : ''}`,
        // mousedown, not click: it fires before the input's blur closes the list.
        onMouseDown: (e: React.MouseEvent) => { e.preventDefault(); pickIndex(i) },
        // A REAL movement only. Chrome sends synthetic mouse events without movement when the
        // list appears or scrolls under a resting pointer — with onMouseEnter that marked the hit
        // under the pointer instead of the current value (operator, 30.09.2026).
        onMouseMove: (e: React.MouseEvent) => {
            if (e.movementX === 0 && e.movementY === 0) return
            if (i !== active) setActive(i)
        },
    })

    return (
        <div className="picker">
            <input
                type="text"
                name={name}
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                aria-required={required || undefined}
                data-required={required ? '' : undefined}
                data-filled={required ? String(!!value) : undefined}
                autoComplete="off"
                placeholder={shown || placeholder}
                value={open ? query : shown}
                onFocus={() => setOpen(true)}
                onBlur={close}
                onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
                onKeyDown={onKeyDown}
            />
            {open && (
                <ul className="picker-list" role="listbox" ref={listRef}>
                    {showClear && (
                        <li {...itemProps(0, !value)}><span className="picker-main">{clearLabel}</span></li>
                    )}
                    {options.map((item, i) => (
                        <li key={keyOf(item)} {...itemProps(i + offset, keyOf(item) === value)}>
                            {renderOption ? renderOption(item) : <span className="picker-main">{labelOf(item)}</span>}
                        </li>
                    ))}
                    {!loading && options.length === 0 && <li className="picker-empty">{TX.noMatches}</li>}
                    {loading && options.length === 0 && <li className="picker-empty">{TX.searching}</li>}
                </ul>
            )}
        </div>
    )
}
